import { DropIndicator } from 'react-aria-components';
import type { DropTarget } from '@react-types/shared';
import { createPortal } from 'react-dom';
import { cn } from 'v5/platform/dom/cn';

export function CollectionDropIndicatorSvg() {
  return (
    <svg
      height={10}
      className="block w-full fill-none forced-colors:stroke-[Highlight]"
      style={{ stroke: 'var(--color-brand-500)' }}
      aria-hidden
    >
      <circle cx={5} cy={5} r={4} strokeWidth={2} />
      <line
        x1={20}
        x2="100%"
        y1={5}
        y2={5}
        strokeWidth={2}
        transform="translate(-10 0)"
      />
      <circle
        cx="100%"
        cy={5}
        r={4}
        strokeWidth={2}
        transform="translate(-5 0)"
      />
    </svg>
  );
}

/** Fixed-position drop line for popover/scroll containers (avoids overflow clipping). */
export function CollectionDropIndicatorFloatingLine({
  rect
}: {
  rect: { top: number; left: number; width: number } | null;
}) {
  if (rect == null || typeof document === 'undefined') {
    return null;
  }

  const container =
    document.getElementById('tailwind-portals') ?? document.body;

  return createPortal(
    <div
      aria-hidden
      className="pointer-events-none fixed z-10000"
      style={{
        top: rect.top,
        left: rect.left,
        width: rect.width,
        transform: 'translateY(-50%)'
      }}
    >
      <CollectionDropIndicatorSvg />
    </div>,
    container
  );
}

/** Zero-layout drop line for RAC collection reorder. Flex lists pair with `-mb-N`; grid lists use item margins instead. */
export function CollectionDropIndicator({
  target,
  gapClassName,
  className,
  lineClassName = '-top-1.5 -translate-y-1/2'
}: {
  target: DropTarget;
  /** Flex lists: `-mb-N` matching parent `gap-N`. Omit for grid lists with `gap-y-0` + item margins. */
  gapClassName?: string;
  className?: string;
  /** Vertical offset — use {@link dndListGapLineClassName} to center in the list gap. */
  lineClassName?: string;
}) {
  return (
    <DropIndicator
      target={target}
      className={cn(
        'pointer-events-none relative z-0 h-0 min-h-0 shrink-0 overflow-visible',
        'data-drop-target:z-50',
        gapClassName,
        'opacity-0 data-drop-target:opacity-100',
        className
      )}
    >
      <div className={cn('absolute inset-x-0 z-50', lineClassName)}>
        <CollectionDropIndicatorSvg />
      </div>
    </DropIndicator>
  );
}
